<script lang="ts" setup>
  import type { ICryptoInvoiceData, ICurrencyData, TDepositMethod } from '~/store/cashier';
  import { LazyModalChangeCrypto } from '#components';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { EDepositMethod, useCashierStore } from '~/store/cashier';
  import copyToClipboard from '~/utils/copyToClipboard';

  const emit = defineEmits<{ changeMethod: [TDepositMethod] }>();

  const cashierStore = useCashierStore();
  const { showErrorToast, showSuccessToast } = useAppToast();

  interface IExpiryData {
    estimatedDate: string | undefined;
    remainingTimeText: string | undefined;
    timerId: NodeJS.Timeout | undefined;
    textClass: string | undefined;
  }

  const EXPIRY_DEFAULT_STATE: IExpiryData = {
    estimatedDate: undefined,
    remainingTimeText: undefined,
    timerId: undefined,
    textClass: '',
  };
  const MINUTES_LEFT_TO_WARNING = 5;
  const INVOICE_RENEWAL_REQUEST_DELAY_MS = 5000;

  const selectedCurrency = ref<ICurrencyData>();
  const selectorOpen = ref<boolean>(true);
  const qrCodeSrc = ref<string>();
  const payAddress = ref<string>();
  const payAmount = ref<number>();
  const exchangeRate = ref<number>();
  const minimalPaymentAmount = ref<number>();
  const expiryData = ref<IExpiryData>({ ...EXPIRY_DEFAULT_STATE });
  const promoCode = ref<string>();

  const networkName = computed(() => {
    if (!selectedCurrency.value) return '';

    const regex = /\((.*?)\)/g;
    const subtitle = selectedCurrency.value.subtitle;
    const matches = [];
    let match;

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(subtitle)) !== null) {
      matches.push(match[1]);
    }

    if (!matches.length) return subtitle;
    return matches[0];
  });

  let isCurrencyRollback = false;

  onBeforeUnmount(() => {
    clearTimeout(expiryData.value.timerId);
  });

  async function processCurrencyChange(newValue: ICurrencyData | undefined, oldValue: ICurrencyData | undefined) {
    if (isCurrencyRollback) {
      isCurrencyRollback = false;
      return;
    }

    if (newValue?.value === oldValue?.value) {
      return;
    }

    if (!newValue?.value) {
      return;
    }

    if (!oldValue?.value) {
      return requestInvoice();
    }

    if (await promptCurrencyChange(oldValue)) {
      qrCodeSrc.value = '';
      await requestInvoice();
    } else {
      isCurrencyRollback = true;
      selectedCurrency.value = oldValue;
    }
  }

  watch(selectedCurrency, processCurrencyChange, { immediate: true });

  async function requestInvoice() {
    if (!selectedCurrency.value) {
      return;
    }

    try {
      const invoice = (await cashierStore.requestInvoice({
        currency: selectedCurrency.value?.value,
      })) as ICryptoInvoiceData;

      if (!invoice) {
        showErrorToast();
        return;
      }

      payAddress.value = invoice.pay_address;
      payAmount.value = invoice.pay_amount;
      exchangeRate.value = Number.parseFloat(invoice.exchange_rate);
      minimalPaymentAmount.value = invoice.minimal_payment_amount;
      expiryData.value.estimatedDate = invoice.expiration_estimate_date;

      if (expiryData.value.estimatedDate) {
        runExpirationTimer();
      }

      await generateQRCode();
    } catch (error: unknown) {
      console.error(error);
      showErrorToast();
    }
  }

  function runExpirationTimer() {
    clearTimeout(expiryData.value.timerId);
    timerTick();
  }

  function timerTick() {
    const date = expiryData.value.estimatedDate || '0000-00-00T00:00:00.000Z';
    const expirationTimestamp = Date.parse(date);
    const diff = expirationTimestamp - Date.now();

    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    expiryData.value.remainingTimeText = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    if (minutes < MINUTES_LEFT_TO_WARNING) {
      expiryData.value.textClass = 'warning';
    }

    if (diff < 0) {
      return handleInvoiceExpiration();
    }

    expiryData.value.timerId = setTimeout(timerTick, 1000);
  }

  function handleInvoiceExpiration() {
    clearTimeout(expiryData.value.timerId);

    const resetState = {
      textClass: 'error',
      remainingTimeText: 'Expired, requesting new invoice',
      timerId: undefined,
    };

    Object.assign(expiryData.value, resetState);
    qrCodeSrc.value = '';
    payAmount.value = undefined;
    payAddress.value = 'Requesting';

    setTimeout(requestInvoice, INVOICE_RENEWAL_REQUEST_DELAY_MS);
  }

  async function generateQRCode() {
    try {
      // Lazy load QRCode library only when needed
      const QRCode = await import('qrcode');
      qrCodeSrc.value = await QRCode.toDataURL(payAddress.value || '', {
        errorCorrectionLevel: 'H',
      });
    } catch (err) {
      qrCodeSrc.value = '';
      console.error(err);
    }
  }

  async function promptCurrencyChange(oldCurrency: ICurrencyData) {
    return new Promise((resolve) => {
      stores.modals.open('change-crypto', LazyModalChangeCrypto, {
        oldCurrency,
        selectedCurrency: selectedCurrency.value,
        onCancel: () => resolve(false),
        onConfirm: () => resolve(true),
      });
    });
  }

  async function copyAddress() {
    try {
      await copyToClipboard(payAddress.value || '');
      showSuccessToast('Success', `Copied to clipboard`);
    } catch (error) {
      console.error(error);
      showErrorToast('Error', `Failed to copy the address`);
    }
  }

  function openTransactionsPage() {
    // eslint-disable-next-line no-alert
    alert('Open Transactions Page is not implemented yet');
  }
</script>

<template>
  <div class="w-full px-5 flex flex-col gap-2.5 mt-4 text-white">
    <CashierCurrencySelector
      v-model:currency="selectedCurrency"
      v-model:open="selectorOpen"
      :list="cashierStore.cryptoCurrencies"
    />

    <div v-if="selectedCurrency && qrCodeSrc && !selectorOpen" class="contents font-barlow text-white">
      <div v-if="payAddress" class="text-white text-xs leading-4">
        <p class="font-semibold">Address:</p>
        <p>{{ payAddress }}</p>
      </div>

      <CoreButton class="h-12" color="primary" @click="copyAddress">
        <span class="font-bold">Copy {{ selectedCurrency.value }} Address</span>
      </CoreButton>

      <img :src="qrCodeSrc" alt="crypto address QR code" class="w-[160px] aspect-square mx-auto rounded-[10px]" />

      <div class="flex flex-col font-barlow text-white">
        <div
          v-if="expiryData.estimatedDate"
          :class="{
            'bg-[#ff5252]': expiryData.textClass === 'error',
            'text-[#ff5722]': expiryData.textClass === 'warning',
          }"
          class="flex px-4 py-3 justify-between items-center self-stretch border-b border-white/30"
        >
          <span v-if="expiryData.textClass !== 'error'" class="text-xs font-normal leading-[120%]">
            Invoice expires in:
          </span>
          <div class="flex gap-1.5">
            <UIcon class="size-4" name="i-mdi-clock-outline" />
            <span class="text-xs font-semibold leading-4 tabular-nums">
              {{ expiryData.remainingTimeText }}
            </span>
          </div>
        </div>
        <div class="flex px-4 py-3 justify-between items-center self-stretch text-xs leading-[120%]">
          <span class="font-normal">Network:</span>
          <span class="font-semibold">{{ networkName }} Network</span>
        </div>
      </div>

      <CashierExchangeCalculator :currency="selectedCurrency" :exchange-rate="exchangeRate" />

      <div class="flex flex-col text-white font-barlow">
        <div class="flex px-2.5 py-2 items-center gap-2.5 self-stretch rounded-t-[6px] bg-white/10">
          <SvgoExclamationCircle class="shrink-0 w-[22px] aspect-square" filled />
          <p class="text-xs font-semibold leading-[150%]">
            Top up addresses are
            <strong class="font-bold">ONE</strong>
            time use only! Don’t reuse it, as your account won’t be credited.
          </p>
        </div>
        <div class="flex px-2.5 py-2 items-center gap-2.5 self-stretch rounded-b-[6px] bg-[#0080ff]/30">
          <SvgoExclamationTriangle class="shrink-0 w-[21px] h-[18px]" filled />
          <p class="text-white font-barlow text-xs font-bold leading-4">
            Deposits less than {{ minimalPaymentAmount }} {{ selectedCurrency?.text }} will NOT be credited to your
            balance
          </p>
        </div>
      </div>

      <p class="text-white font-barlow text-xs font-normal leading-[150%]">
        Send {{ selectedCurrency?.text }} ONLY on the {{ networkName }} network to this address or your funds may be
        lost.
      </p>

      <p class="text-white font-barlow text-xs font-normal leading-[150%]">
        {{ selectedCurrency?.text }} deposits can take some time to process on the blockchain. You can check its status
        on your
        <NuxtLink class="font-medium cursor-pointer underline" @click="openTransactionsPage">transactions</NuxtLink>
        page.
      </p>

      <UInput
        v-model="promoCode"
        :ui="{
          base: 'h-11 px-4 py-3 text-[#101317] font-barlow text-sm font-medium leading-[120%] placeholder:text-[#101317] rounded-[.375rem]',
          leading: 'pointer-events-none pl-5 pt-[3px]',
          trailing: 'p-0',
        }"
        variant="subtle"
        placeholder="Promo Code (Optional)"
      >
        <template #trailing>
          <div class="flex items-center justify-center w-[66px] h-full px-[26px] py-4 rounded-r-[5px] bg-[#0CBC47]">
            <UIcon class="shrink-0 size-6 text-white" name="i-lucide-check" />
          </div>
        </template>
      </UInput>
    </div>
    <div v-else-if="!selectorOpen" class="spinner-wrapper">
      <CoreLoadingSpinner v-if="selectedCurrency" class="py-12" text-color="#fff" />
    </div>

    <UButton
      :ui="{
        base: 'w-full h-[48px] rounded-6px font-barlow text-base font-medium] gap-0.5',
      }"
      color="neutral"
      @click="emit('changeMethod', EDepositMethod.Card)"
    >
      <UIcon class="size-4" name="lucide:chevron-left" />
      <span>Go Back</span>
    </UButton>
  </div>
</template>
